<template lang="pug">
    div(
        v-if="group.product"
        class="my-16"
    )
        div(class="grid mx-auto gap-x-6 md:grid-rows-[auto,1fr] md:grid-cols-[40%,1fr] xl:w-full xl:grid-cols-[auto, 1fr]")
            div(class="order-1 md:row-span-2")
                a(@click="showLightbox()")
                    img(
                        :key="`${group.product.gallery[0].filename}-${group.product.gallery[0].key}`"
                        :src="$getImageUrl(group.product.gallery[0].key, group.product.gallery[0].filename, 630, 472, 70, 'fff')"
                        :alt="group.product.gallery[0].title"
                        width="630"
                        height="472"
                        class="m-auto md:w-full"
                        loading="lazy"
                    )
            div(class="order-first pb-3 md:pb-0 md:order-2")
                h2(v-html="group.title" class="uppercase mt-0 mb-3")
                Reviews(:data="group.product")
                Pricing(:data="group.product")

            div(class="order-3 py-3")
                div(class="md:flex -m-3 mb-3")
                    div(
                        class="p-3"
                        :class="group.product.hasOverriddenFeatureBullets || group.product.hasOverriddenInstallGuide || group.product.orderBullets ? 'md:w-1/2' : ''"
                    )
                        //- https://www.realtruck.com/flowmaster-american-thunder-exhaust-system/R182337P2018Y734MA.html example of group stuff
                        Info(:data="group.product" isGroupProduct)
                    div(
                        v-if="group.product.hasOverriddenFeatureBullets || group.product.hasOverriddenInstallGuide || group.product.orderBullets"
                        class="p-3 md:w-1/2"
                    )
                        //- .content is a special global class to format html tags
                        div(class="content")
                            p #[strong Additional Information]
                            div(v-if="group.product.hasOverriddenFeatureBullets" v-html="group.product.features.content")
                            ul(v-if="group.product.orderBullets")
                                li(v-for="bullet in group.product.orderBullets" v-html="bullet")
                            div(v-if="group.product.hasOverriddenInstallGuide && group.product.installation && group.product.installation.files && group.product.installation.files.length == 1")
                                a(:href="getFileUrl(group.product.installation.files[0].folder,group.product.installation.files[0].filename)" target="_blank") Download Installation Instructions

                div(data-testid="groupDrillSelection")
                    Drill(
                        v-for="drill in group.drills"
                        :id="`scrollTarget-${arrayIndex}-${drill.type}`"
                        :key="drill.type"
                        :drill="drill"
                        :currentValue="drill.currentValue"
                        @changed="drillChanged"
                        @changedNative="resetControls"
                        @click.native="alert(drill.isDisabled, arrayIndex)"
                    )

                ProductMessage(v-if="group.hasOrderControlError" :showErrorIcon="true")

                template(v-else-if="group.product.isDealerOnly")
                    DealerLocatorLink(:data="group.product")

                template(v-else)
                    div(class="md:max-w-[472px]")
                        div(class="space-y-2")
                            AddToCart(
                                :disabled="isDisabled"
                                :isOutOfStock="group.product.isOutOfStock"
                                :sku="group.product.sku"
                                :qtySelector="qtySelector"
                                :isPreOrder="group.product.isPreOrder"
                                @click.native="alert(isDisabled, arrayIndex)"
                            )
                            BackInStockNotification(
                                v-if="$sitewideData.config.backInStockNotificationEnabled && group.product.isOutOfStock && !group.product.isDisabled"
                                :productLineSlug="group.product.productLineSlug"
                                :skuSlug="group.product.skuSlug"
                            )

                        template(v-if="group.product.actualPrice")
                            AffirmAsLowAs(
                                v-if="$affirm.enabled"
                                :price="group.product.actualPrice.min"
                                pageType="product"
                                alignment="left"
                            )
                            PaypalMessaging(
                                v-if="$paypal.enabled"
                                :price="group.product.actualPrice.min"
                                pageType="product"
                            )

        Lightbox(
            v-if="isOpen"
            :items="lightboxItems"
            :uniqueKey="`imageList_${this.arrayIndex}`"
            :options="lightboxOptions"
            @close="hideLightbox"
        )

</template>
<script>
import Card from '@/components/style/card'
import { getFileUrl } from '../_helpers/getFileUrl'
import Reviews from './reviews'
import Drill from './drill'
import AddToCart from './addToCart'
import Info from './info'
import Pricing from './pricing'
import DealerLocatorLink from './dealerLocatorLink'

const AffirmAsLowAs = () =>
    import(/* webpackChunkName: "affirm-as-low-as" */ '@/integrations/affirm/affirmAsLowAs')
const PaypalMessaging = () =>
    import(/* webpackChunkName: "paypal-messaging" */ '@/integrations/paypal/messaging')
const Lightbox = () =>
    import(/* webpackChunkName: "photoswipe-lightbox" */ '@/components/overlay/photoswipeLightbox')
const ProductMessage = () => import(/* webpackChunkName: "product-message" */ './productMessage')
const BackInStockNotification = () =>
    import(/* webpackChunkName: "back-in-stock-notification" */ './backInStockNotification')

export default {
    components: {
        Card,
        Drill,
        AddToCart,
        Info,
        DealerLocatorLink,
        BackInStockNotification,
        AffirmAsLowAs,
        PaypalMessaging,
        Lightbox,
        ProductMessage,
        Reviews,
        Pricing,
    },
    inject: ['alert'],
    props: {
        arrayIndex: {
            required: true,
            type: Number,
        },
        group: {
            required: true,
            type: Object,
        },
        qtySelector: {
            // a pass-through :(
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            lightboxOptions: {
                index: 0,
                closeOnScroll: false,
                history: false,
                shareEl: false,
                fullscreenEl: false,
            },
        }
    },
    computed: {
        isDisabled() {
            return !this.group.product.sku || this.group.product.isOutOfStock
        },
        isOpen() {
            return this.$overlay.state.activeOverlay === `imageList_${this.arrayIndex}`
        },
        lightboxItems() {
            return (
                this.group.product.gallery.map((imageData) => {
                    const width = this.$device.isMobile ? 1024 : 1920
                    const height = this.$device.isMobile ? 768 : 1080
                    const imgObj = {
                        src: this.$getImageUrl(
                            imageData.key,
                            imageData.filename,
                            width,
                            height,
                            80,
                            '000'
                        ),
                        w: width,
                        h: height,
                        title: imageData.title,
                    }
                    // create new image array with image objects in format that photoswipe likes
                    return imgObj
                }) || []
            )
        },
    },
    methods: {
        getFileUrl,
        // we could have this comp just emit the event and pass up to index.vue to handle... meh for now
        drillChanged(obj) {
            this.$store.dispatch('product/fillNextDrill', obj) // can we do this in the template?
        },
        resetControls(obj) {
            this.$store.dispatch('product/resetControls', obj) // reset controls 2 below this one
        },
        showLightbox() {
            this.$overlay.show(`imageList_${this.arrayIndex}`)
            this.$set(this.lightboxOptions, 'index', 0)
        },
        hideLightbox() {
            this.$overlay.hide()
        },
    },
}
</script>
